import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Pages
import LandingPage from './pages/LandingPage';
import BrowseProjects from './pages/BrowseProjects';
import ProjectDetail from './pages/ProjectDetail';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import Login from './pages/Login';
import Signup from './pages/Signup';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// Talent Pages
import TalentDashboard from './pages/talent/Dashboard';
import TalentOnboarding from './pages/talent/Onboarding';
import TalentApplications from './pages/talent/Applications';
import TalentProfileEdit from './pages/talent/ProfileEdit';
import TalentSettings from './pages/talent/Settings';

// Company Pages
import CompanyDashboard from './pages/company/Dashboard';
import CompanyOnboarding from './pages/company/Onboarding';
import CompanyProjects from './pages/company/Projects';
import CompanyPostProject from './pages/company/PostProject';
import CompanyProjectDetail from './pages/company/ProjectDetail';
import CompanySettings from './pages/company/Settings';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminTalent from './pages/admin/Talent';
import AdminCompanies from './pages/admin/Companies';
import AdminProjects from './pages/admin/Projects';
import AdminApplications from './pages/admin/Applications';
import AdminUsers from './pages/admin/Users';

import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
      setProfileChecked(true);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Fetch profile based on role - wait for profile before setting loading to false
      if (response.data.role === 'talent') {
        try {
          const profileRes = await axios.get(`${API}/talent/profile`);
          setProfile(profileRes.data);
        } catch (e) {
          setProfile(null);
        }
      } else if (response.data.role === 'company') {
        try {
          const profileRes = await axios.get(`${API}/company/profile`);
          setProfile(profileRes.data);
        } catch (e) {
          setProfile(null);
        }
      }
      setProfileChecked(true);
      setLoading(false);
    } catch (error) {
      logout();
      setLoading(false);
      setProfileChecked(true);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    // Fetch profile immediately after login for talent/company users
    if (userData.role === 'talent') {
      try {
        const profileRes = await axios.get(`${API}/talent/profile`);
        setProfile(profileRes.data);
      } catch (e) {
        setProfile(null);
      }
    } else if (userData.role === 'company') {
      try {
        const profileRes = await axios.get(`${API}/company/profile`);
        setProfile(profileRes.data);
      } catch (e) {
        setProfile(null);
      }
    }
    setProfileChecked(true);
    
    return userData;
  };

  const signup = async (email, password, role) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user?.role === 'talent') {
      try {
        const profileRes = await axios.get(`${API}/talent/profile`);
        setProfile(profileRes.data);
      } catch (e) {
        setProfile(null);
      }
    } else if (user?.role === 'company') {
      try {
        const profileRes = await axios.get(`${API}/company/profile`);
        setProfile(profileRes.data);
      } catch (e) {
        setProfile(null);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, profile, profileChecked, login, signup, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, profile, profileChecked } = useAuth();
  const location = useLocation();

  // Wait for both auth and profile check to complete
  if (loading || !profileChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          <p className="text-sm text-stone-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'company') return <Navigate to="/company/dashboard" replace />;
    if (user.role === 'talent') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  // Check if onboarding is needed (only after profile has been checked)
  if (user.role === 'talent' && !profile && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  if (user.role === 'company' && !profile && location.pathname !== '/company/onboarding') {
    return <Navigate to="/company/onboarding" replace />;
  }

  return children;
};

// Public Route (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'company') return <Navigate to="/company/dashboard" replace />;
    if (user.role === 'talent') return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors duration={3000} closeButton />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/projects" element={<BrowseProjects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
          
          {/* Talent Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['talent']}>
              <TalentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute allowedRoles={['talent']}>
              <TalentOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/applications" element={
            <ProtectedRoute allowedRoles={['talent']}>
              <TalentApplications />
            </ProtectedRoute>
          } />
          <Route path="/profile/edit" element={
            <ProtectedRoute allowedRoles={['talent']}>
              <TalentProfileEdit />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute allowedRoles={['talent']}>
              <TalentSettings />
            </ProtectedRoute>
          } />
          
          {/* Company Routes */}
          <Route path="/company/dashboard" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company/onboarding" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/company/projects" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyProjects />
            </ProtectedRoute>
          } />
          <Route path="/company/projects/new" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyPostProject />
            </ProtectedRoute>
          } />
          <Route path="/company/projects/:id" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyProjectDetail />
            </ProtectedRoute>
          } />
          <Route path="/company/settings" element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanySettings />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/talent" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTalent />
            </ProtectedRoute>
          } />
          <Route path="/admin/companies" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCompanies />
            </ProtectedRoute>
          } />
          <Route path="/admin/projects" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminProjects />
            </ProtectedRoute>
          } />
          <Route path="/admin/applications" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminApplications />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminUsers />
            </ProtectedRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
